import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { GeaoContext } from "../../../GeaoContextProvider";
import { ApiRequestStatus } from "../../../constants/ApiRequestStatus";
import { GeaoPaths } from "../../../constants/GeaoPaths";
import { PageStatus } from "../../../hooks/useInterviewUpdater";

import { formDataToQuestionAnswers } from "./mappers";
import { update } from "../../../../../api/onboarding";

/**
 * Custom hook for handling form events in the Producer Personal Information component.
 * Manages form submission, save and exit functionality, and API update operations.
 * 
 * @param {Function} [getValues=() => ({})] - Function to retrieve current form values
 * @param {Function} [trigger=() => Promise.resolve()] - Function to trigger form validation
 * @param {Array} [branchOptions=[]] - Array of branch options for parent branch selection
 * @param {string} branchOptions[].value - The source of business value for the branch
 * @param {string} branchOptions[].interviewId - The interview ID associated with the branch
 * 
 * @returns {Object} Object containing form event handlers and state
 * @returns {Function} returns.onSubmit - Handler for form submission that progresses to next step
 * @returns {Function} returns.onSaveAndExit - Handler for save and exit functionality
 * @returns {boolean} returns.saving - Flag indicating if an API update is in progress
 * 
 * @example
 * const { onSubmit, onSaveAndExit, saving } = useFormEvents(
 *   getValues,
 *   trigger,
 *   branchOptions
 * );
 */
export default function useFormEvents(
    getValues = () => ({}),
    trigger = () => { return Promise.resolve(); },
    branchOptions = []
) {
    /** @type {Function} Navigation function from react-router-dom */
    const navigate = useNavigate();

    /** @type {Object} Context containing agent interview data and setter function */
    const { agentInterview, setAgentInterview } = useContext(GeaoContext);

    /** @type {[ApiRequestStatus, Function]} State for tracking API update request status */
    const [updateStatus, setUpdateStatus] = useState(ApiRequestStatus.NotSent);

    /** @type {[string, Function]} State for tracking the next navigation path */
    const [nextPath, setNextPath] = useState(GeaoPaths.CreateYourProfileChecklist);

    /** @type {boolean} Computed property indicating if an API update is in progress */
    const saving = updateStatus === ApiRequestStatus.Pending;

    /**
     * Effect hook that handles navigation after successful API updates.
     * Monitors the update status and navigates to the appropriate path when the update succeeds.
     * 
     * @listens {ApiRequestStatus} updateStatus - Triggers when API request status changes
     * @listens {string} nextPath - Triggers when the next navigation path changes
     */
    useEffect(() => {
        if (updateStatus != ApiRequestStatus.Success) return;

        navigate(nextPath);
    }, [updateStatus, nextPath]);

    /**
     * Handles form submission and progresses to the next step in the onboarding flow.
     * Sets the next path to Producer Background Information and updates the interview.
     * 
     * @param {Object} formData - The form data to be submitted
     * @param {string} [formData.parentBranchSob] - Parent branch source of business (optional)
     */
    const onSubmit = (formData) => {
        setNextPath(GeaoPaths.ProducerBackgroundInformation);
        updateInterview(formData, false);
    };

    /**
     * Handles save and exit functionality.
     * Triggers form validation, then saves current form state and navigates back to checklist.
     * 
     * @returns {Promise<void>} Promise that resolves when the save operation is complete
     */
    const onSaveAndExit = () => {
        trigger().then(_ => {
            setNextPath(GeaoPaths.CreateYourProfileChecklist);
            updateInterview(getValues(), true);
        });
    };

    /**
     * Updates the agent interview with form data and manages parent branch relationships.
     * 
     * @param {Object} formData - The form data to be processed and saved
     * @param {string} [formData.parentBranchSob] - Parent branch source of business (optional)
     * @param {boolean} isSaveAndExit - Flag indicating if this is a save and exit operation
     * 
     * @description
     * This function:
     * 1. Prevents duplicate API calls if one is already in progress
     * 2. Creates activity request for save and exit operations
     * 3. Manages parent branch relationships by removing existing ones and adding new ones if needed
     * 4. Updates the interview with new question answers and page status
     * 5. Handles API success/error states
     */
    const updateInterview = (formData, isSaveAndExit) => {
        if (updateStatus == ApiRequestStatus.Pending) return;

        /** @type {Object|null} Activity request object for tracking save and exit operations */
        const activityRequest = isSaveAndExit ? {
            ActivityCategory: "GEAO_ONBOARDING",
            ActivitySubType: "SAVE_AND_EXIT",
            SourceApplication: "AGIWUI",
            AdditionalData: {
                ComponentName: "ProducerPersonalInformation"
            }
        } : null;

        /** @type {string|undefined} Source of business for the parent agency relationship */
        const parentAgencySob = agentInterview?.relationships?.find(x => x.type === "ParentAgency")?.sourceOfBusiness;

        /** @type {Array} Filtered relationships excluding existing ParentBranch relationships */
        let relationships = agentInterview?.relationships?.filter(r => r.type !== "ParentBranch");

        // Add new ParentBranch relationship only if parentBranchSob exists and is different from parentAgencySob
        if (formData.parentBranchSob && formData.parentBranchSob !== parentAgencySob) {
            /** @type {Object|undefined} Selected branch option matching the form data */
            const selectedBranchOption = branchOptions?.find(o => o.value === formData.parentBranchSob);

            /** @type {Object} New parent branch relationship object */
            const parentBranchRelationship = {
                type: "ParentBranch",
                sourceOfBusiness: formData.parentBranchSob,
                interviewId: selectedBranchOption?.interviewId
            };

            relationships = [...relationships, parentBranchRelationship];
        }

        /** @type {Object} Complete update request object for the agent interview */
        const updateAgentInterviewRequest = {
            activityRequest,
            identityType: "Agent",
            interview: {
                ...agentInterview,
                questionAnswers: {
                    ...agentInterview?.questionAnswers,
                    ...formDataToQuestionAnswers(formData)
                },
                relationships,
                pageStatus: {
                    ...agentInterview.pageStatus,
                    "ProducerPersonalInformation": PageStatus.Completed
                }
            }
        }

        setUpdateStatus(ApiRequestStatus.Pending);

        update(updateAgentInterviewRequest,
            /**
             * Success callback - handles successful API response
             * @param {Object} interview - Updated interview object from the API
             */
            interview => {
                setAgentInterview(interview);
                setUpdateStatus(ApiRequestStatus.Success);
            },
            /**
             * Finally callback - executed after success or error
             */
            () => {
                // No cleanup actions needed at this time
            },
            /**
             * Error callback - handles API request failures
             * @param {Error} error - Error object from failed API request
             */
            _ => {
                setUpdateStatus(ApiRequestStatus.Error);
            });
    };

    return {
        onSubmit,
        onSaveAndExit,
        saving
    };
}